import { create } from 'zustand';

import { IDebugDataObj, IDebugStoreData, IDebugStoreExportData, IExportItemDataObj, TService } from "~/interfaces";
import { PERSONS_KEYS } from "~/persons/makuderovi";

interface IDebugStore {
	debug: {
		data: IDebugStoreData;
		exportData: IDebugStoreExportData;
		lastPersons: Array<Array<string>>;
		allPersons: Array<string>;
		// pouzite tagy - rychla tlacitka v editaci
		usedTags: Array<string>;
		overwriteData: boolean;
		service: TService;
	};
	addChange: (seo: string, changes: IDebugDataObj, exportChanges: IExportItemDataObj) => void;
	setLastPersons: (lastPersons: Array<string>) => void;
	addUsedTags: (tags: Array<string>) => void;
	setOverwriteData: (overwriteData: boolean) => void;
	clearLastPersons: () => void;
	setService: (service: TService) => void;
}

const DEF_PERSONS_MAKUDEROVI = [PERSONS_KEYS.ROM_MAK, PERSONS_KEYS.LEN_MAK, PERSONS_KEYS.MAR_MAK, PERSONS_KEYS.TOM_MAK];
const DEF_PERSONS_ROMAN = [PERSONS_KEYS.ROM_MAK];
const DEF_PERSONS_LENKA = [PERSONS_KEYS.LEN_MAK];

function getPersonsByService(service: TService) {
	switch (service) {
		case "makuderovi":
			return DEF_PERSONS_MAKUDEROVI;

		case "roman":
			return DEF_PERSONS_ROMAN;

		case "lenka":
			return DEF_PERSONS_LENKA;

		default:
			return [];
	}
}

export const debugStore = create<IDebugStore>(set => ({
	debug: {
		data: {},
		exportData: {},
		lastPersons: [],
		allPersons: [],
		usedTags: [],
		overwriteData: false,
		service: "makuderovi",
	},
	addChange: (seo, changes, exportChanges) => set(state => {
		const curData = state.debug.data;
		const curExportData = state.debug.exportData;

		// oba objekty - seo
		if (!(seo in curData)) {
			curData[seo] = {};
		}

		if (!(seo in curExportData)) {
			curExportData[seo] = {};
		}

		const allChanges = Object.keys(changes);
		const allChangesExport = Object.keys(exportChanges);

		for (const name of allChanges) {
			if (!(name in curData[seo])) {
				curData[seo][name] = {};
			}

			Object.assign(curData[seo][name], changes[name]);
		}

		for (const name of allChangesExport) {
			if (!(name in curExportData[seo])) {
				curExportData[seo][name] = {};
			}

			Object.assign(curExportData[seo][name], exportChanges[name]);
		}

		return {
			debug: {
				...state.debug,
				data: curData,
			},
		};
	}),
	setLastPersons: lastPersons => set(state => {
		if (lastPersons.length === 0) {
			return state;
		}

		const len = state.debug.lastPersons.length;
		const stateLastPersons = state.debug.lastPersons.slice();
		const allPersons = state.debug.allPersons.slice();

		// preskocime ty, ktere tam uz jsou
		if (len > 0) {
			const lastTestKey = stateLastPersons[stateLastPersons.length - 1].join("|");
			const newKey = lastPersons.join("|");

			if (newKey !== lastTestKey) {
				stateLastPersons.push(lastPersons);
			}
		} else {
			stateLastPersons.push(lastPersons);
		}

		for (const person of lastPersons) {
			if (!allPersons.includes(person)) {
				allPersons.push(person);
			}
		}

		return {
			debug: {
				...state.debug,
				lastPersons: stateLastPersons,
				allPersons,
			},
		};
	}),
	addUsedTags: tags => set(state => {
		const newTags = tags.filter(tag => !state.debug.usedTags.includes(tag));

		if (newTags.length === 0) {
			return state;
		}

		return {
			debug: {
				...state.debug,
				usedTags: [...state.debug.usedTags, ...newTags],
			},
		};
	}),
	clearLastPersons: () => set(state => ({
		debug: {
			...state.debug,
			allPersons: getPersonsByService(state.debug.service),
			lastPersons: [],
		},
	})),
	setOverwriteData: overwriteData => set(state => ({
		debug: {
			...state.debug,
			overwriteData,
		},
	})),
	setService: service => set(state => ({
		debug: {
			...state.debug,
			service,
			allPersons: getPersonsByService(service),
		},
	})),
}));

export function debugStoreInit(service: TService) {
	debugStore.getState().setService(service);
}
